import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { axiosGet, axiosPut } from "api";
import OperationPrintDetails from "components/Prints/Operations/OperationPrintDetails";
import OperationPrintHeaderDetails from "components/Prints/Operations/OperationPrintHeaderDetails";
import WorkOrderOperationsDetails from "components/Prints/Operations/WorkOrderOperationsPrintDetails";
import WorkOrderMovement, {
  WorkOrderMovementHandle,
} from "components/OperationsPage/workOrders/WorkOrderMovement";
import { PrintLayout } from "components/Prints/PrintLayout";
import {
  buildObservationRecordItemsFromStockUnits,
  type ArticleLocationObservationRecordItem,
} from "helpers/articleLocationObservationRecords";
import { defaultMovement, MovementTypes, Roles, ROUTES } from "constants/index";
import React, { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MdAdd, MdDelete, MdEdit, MdExpandMore, MdPrint, MdVisibility } from "react-icons/md";
import { useReactToPrint } from "react-to-print";
import {
  ArticleLocation,
  ArticleLocationObservationRecordStatus,
  ArticleLocationObservationRecordStockUnit,
  Client,
  IMovementWorkOrderDetail,
  Movement,
  MovementArticle,
  Store,
  User,
  AssociatedClient,
  OrderStatus,
} from "types";
import EditMovementModal from "./EditMovementModal";
import { formatDateOnlyDMY } from "helpers";
import { useNavigate } from "react-router-dom";
import InventoryAdjustmentModal from "./InventoryAdjustmentModal";
import useModals from "hooks/useModals";
import DeleteConfirmationModaL from "components/utils/DeleteConfirmationModal";
import ObservationRecordsModal from "components/utils/ObservationRecordsModal";
import { ENTITY } from "constants/entities";
import useCrud from "hooks/useCrud";
import {
  getArticleLocationObservationRecordsUrl,
  getRelatedAdditionalMovementsUrl,
  MOVEMENT_ARTICLES_URL,
  MOVEMENTS_URL,
} from "constants/urls";
import { OperationPrintTotals } from "components/Prints/Operations/OperationPrintTotal";
import MovementAdditionalItemsStepModal, {
  MovementAdditionalItemsConfirmPayload,
} from "components/Operations/MovementAdditionalItemsStepModal";
import { useSession } from "providers/SessionProvider";
import { useToast } from "hooks/useToast";
import {
  buildAdditionalMovementsPayload,
  getAdditionalItemsModalTitle,
} from "helpers/additionalMovements";
import { getMovementArticleStockStateLabel } from "helpers/stockStatus";
import { getOrderStatusLabel } from "helpers/orders";

type Props = {
  movement: Movement;
  stores: Store[];
  client: Client | null;
  onUpdate: (movement: Movement) => void;
  onDelete: (id: number) => void;
  users?: User[];
  associatedClients: AssociatedClient[];
  onCancel: (
    id: number,
    restoreTargetStatus?: "available" | "not_available",
  ) => Promise<boolean>;
  onAdditionalMovementsCreated?: (movements: Movement[]) => void;
  hideRelatedMovementAccordions?: boolean;
};

const operationTypeName = (operationType: number, orderStatus?: OrderStatus) => {
  switch (operationType) {
    case MovementTypes.Income:
      return "Ingreso";
    case MovementTypes.Discharge:
      return "Egreso";
    case MovementTypes.Additional:
      return "Adicionales";
    case MovementTypes.Preparation: {
      const orderStatusLabel = getOrderStatusLabel(orderStatus);

      return orderStatusLabel === "-" ? "Pedido" : `Pedido ${orderStatusLabel}`;
    }
    case MovementTypes.Transfer:
      return "Transferencia";
    case MovementTypes.IncomeDraft:
      return "Ingreso (Borrador)";
    case MovementTypes.DischargeDraft:
      return "Egreso (Borrador)";
    case MovementTypes.PreparationDraft:
      return "Pedido a preparar (Borrador)";
    case MovementTypes.IncomeAdjustment:
      return "Ingreso (Ajuste)";
    case MovementTypes.DischargeAdjustment:
      return "Egreso (Ajuste)";
    case MovementTypes.WorkOrder:
      return "Orden de trabajo";
    default:
      return "-";
  }
};

const operationTypeChipColor = (operationType: number) => {
  switch (operationType) {
    case MovementTypes.Income:
    case MovementTypes.IncomeAdjustment:
      return "success";
    case MovementTypes.Discharge:
    case MovementTypes.DischargeAdjustment:
      return "error";
    case MovementTypes.Additional:
      return "info";
    case MovementTypes.Preparation:
      return "warning";
    case MovementTypes.Transfer:
      return "secondary";
    case MovementTypes.WorkOrder:
      return "info";
    default:
      return "default";
  }
};

type MovementObservationFetchContext = {
  status: ArticleLocationObservationRecordStatus;
  income_id?: number | null;
  reserved_movement_id?: number | null;
  discharged_movement_id?: number | null;
};

const getMovementArticleObservationFetchContexts = (
  movement: Movement,
  movementArticle: MovementArticle,
): MovementObservationFetchContext[] => {
  const articleLocation = movementArticle.article_locations;

  if (!articleLocation) {
    return [];
  }

  if (
    movement.movement_type_id === MovementTypes.Income ||
    movement.movement_type_id === MovementTypes.IncomeDraft ||
    movement.movement_type_id === MovementTypes.IncomeAdjustment
  ) {
    return [
      {
        status: "available",
        income_id: Number(movement.id),
      },
    ];
  }

  if (
    movement.movement_type_id === MovementTypes.Preparation ||
    movement.movement_type_id === MovementTypes.PreparationDraft
  ) {
    return [
      {
        status: "reserved",
        reserved_movement_id: Number(movement.id),
      },
      {
        status: "discharged",
        reserved_movement_id: Number(movement.id),
      },
    ];
  }

  if (
    movement.movement_type_id === MovementTypes.Discharge ||
    movement.movement_type_id === MovementTypes.DischargeAdjustment
  ) {
    return [
      {
        status: "discharged",
        discharged_movement_id: Number(movement.id),
      },
    ];
  }

  const stock = Number(articleLocation.stock ?? 0);
  const reserved = Number(articleLocation.reserved ?? 0);
  const notAvailable = Number(articleLocation.not_available ?? 0);
  const available = Math.max(0, stock - reserved - notAvailable);
  const contexts: MovementObservationFetchContext[] = [];

  if (available > 0) {
    contexts.push({ status: "available" });
  }

  if (notAvailable > 0) {
    contexts.push({ status: "not_available" });
  }

  if (!contexts.length) {
    contexts.push({ status: "available" });
  }

  return contexts;
};

const buildObservationRecordsQueryParams = (
  context: MovementObservationFetchContext,
) => ({
  status: context.status,
  ...(Number(context.income_id ?? 0) > 0
    ? { income_id: Number(context.income_id) }
    : {}),
  ...(context.status === "reserved" &&
  Number(context.reserved_movement_id ?? 0) > 0
    ? { reserved_movement_id: Number(context.reserved_movement_id) }
    : {}),
  ...(context.status === "discharged" &&
  Number(context.discharged_movement_id ?? 0) > 0
    ? { discharged_movement_id: Number(context.discharged_movement_id) }
    : {}),
  ...(context.status === "discharged" &&
  Number(context.reserved_movement_id ?? 0) > 0
    ? { reserved_movement_id: Number(context.reserved_movement_id) }
    : {}),
});

const resolveFetchedMovement = (data: unknown): Movement | null => {
  if (Array.isArray(data)) {
    return (data[0] as Movement | undefined) ?? null;
  }

  if (data && typeof data === "object" && "result" in data) {
    const result = (data as { result?: Movement[] }).result;

    return Array.isArray(result) ? result[0] ?? null : null;
  }

  return (data as Movement | null | undefined) ?? null;
};

export default function OperationsLogsCard({
  movement,
  stores,
  client,
  onUpdate,
  onDelete,
  users,
  associatedClients,
  onCancel,
  onAdditionalMovementsCreated,
  hideRelatedMovementAccordions = false,
}: Props) {
  const [movementToDelete, setMovementToDelete] = useState<number | undefined>(
    undefined
  );
  const [movementToCancel, setMovementToCancel] = useState<number | undefined>(
    undefined
  );
  const [orderRestoreStatus, setOrderRestoreStatus] = useState<
    "available" | "not_available"
  >("available");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [isCancelledLocally, setIsCancelledLocally] = useState(false);

  const { deleteModalOpen, setDeleteModalOpen } = useModals();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [additionalItemsModalOpen, setAdditionalItemsModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editableMovement, setEditableMovement] = useState<Movement | null>(
    null,
  );

  const { createData: createMovement } = useCrud<Movement[]>(MOVEMENTS_URL, {
    disableAutoFetch: true,
  });
  const { fetchData: getMovementById } = useCrud<Movement>(MOVEMENTS_URL, {
    disableAutoFetch: true,
  });
  const { fetchData: getRelatedAdditionalMovements } = useCrud<Movement>(
    getRelatedAdditionalMovementsUrl(Number(movement.id)),
    { disableAutoFetch: true },
  );
  const { fetchData: getMovementArticlesByMovementId } =
    useCrud<MovementArticle>(MOVEMENT_ARTICLES_URL, {
      disableAutoFetch: true,
    });

  const [orderMovement, setOrderMovement] = useState<Movement | null>(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderAccordionOpen, setOrderAccordionOpen] = useState(false);
  const [dischargeMovement, setDischargeMovement] = useState<Movement | null>(
    null,
  );
  const [dischargeLoading, setDischargeLoading] = useState(false);
  const [dischargeAccordionOpen, setDischargeAccordionOpen] = useState(false);
  const [relatedAdditionalMovements, setRelatedAdditionalMovements] = useState<
    Movement[] | null
  >(null);
  const [relatedAdditionalsLoading, setRelatedAdditionalsLoading] =
    useState(false);
  const [relatedAdditionalsAccordionOpen, setRelatedAdditionalsAccordionOpen] =
    useState(false);
  const [articlesAccordionOpen, setArticlesAccordionOpen] = useState(false);
  const [additionalItemsAccordionOpen, setAdditionalItemsAccordionOpen] =
    useState(false);
  const [movementArticlesDetail, setMovementArticlesDetail] = useState<
    MovementArticle[] | null
  >(null);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [selectedObservationArticle, setSelectedObservationArticle] =
    useState<MovementArticle | null>(null);
  const [observationRecordsOpen, setObservationRecordsOpen] = useState(false);
  const [observationRecordsLoading, setObservationRecordsLoading] =
    useState(false);
  const [observationRecords, setObservationRecords] = useState<
    ArticleLocationObservationRecordItem[]
  >([]);
  const [savingObservationRecordId, setSavingObservationRecordId] = useState<
    string | null
  >(null);
  const [printMovement, setPrintMovement] = useState<Movement | null>(null);
  const [printWorkOrder, setPrintWorkOrder] = useState<IMovementWorkOrderDetail | null>(null);
  const [printRelatedAdditionalMovements, setPrintRelatedAdditionalMovements] =
    useState<Movement[]>([]);
  const [printLoading, setPrintLoading] = useState(false);
  const [printPending, setPrintPending] = useState(false);
  const { clients, additionalItems, userData } = useSession();
  const { showSnackBar } = useToast();
  const isClient = userData?.role === Roles.CLIENT;

  const childClients = useMemo(
    () =>
      clients.filter(
        (client) => client.parent_client_id === movement.client_id,
      ),
    [clients, movement.client_id],
  );

  const scopedClientIds = useMemo(
    () => [
      movement.client_id,
      ...childClients.map((client) => client.id),
    ],
    [movement.client_id, childClients],
  );

  const clientAdditionalItems = useMemo(
    () =>
      additionalItems.filter(
        (item) =>
          item.client_id == null || scopedClientIds.includes(item.client_id),
      ),
    [additionalItems, scopedClientIds],
  );

  const shouldShowOrderAccordion =
    !hideRelatedMovementAccordions &&
    movement.movement_type_id === MovementTypes.Discharge && !!movement.order_id;
  const shouldShowDischargeAccordion =
    !hideRelatedMovementAccordions &&
    movement.movement_type_id === MovementTypes.Preparation &&
    !!movement.discharge_movement_id;
  const shouldShowArticlesAccordion =
    movement.movement_type_id !== MovementTypes.Additional &&
    movement.movement_type_id !== MovementTypes.Transfer &&
    movement.movement_type_id !== MovementTypes.WorkOrder;
  const isWorkOrder = movement.movement_type_id === MovementTypes.WorkOrder;
  const shouldShowAdditionalItemsAccordion =
    (movement.additional_items?.length ?? 0) > 0;
  const shouldShowRelatedAdditionals =
    !isClient &&
    !hideRelatedMovementAccordions &&
    (movement.movement_type_id === MovementTypes.Income ||
      movement.movement_type_id === MovementTypes.Discharge) &&
    (movement.additional_movement_ids?.length ?? 0) > 0;
  const relatedAdditionalMovementIds = Array.from(
    new Set(movement.additional_movement_ids ?? []),
  ).sort((a, b) => a - b);
  const relatedAdditionalMovementIdsKey = relatedAdditionalMovementIds.join(",");
  const isIncomeLikeMovement =
    movement.movement_type_id === MovementTypes.Income ||
    movement.movement_type_id === MovementTypes.IncomeDraft ||
    movement.movement_type_id === MovementTypes.IncomeAdjustment;
  const isDischargeLikeMovement =
    movement.movement_type_id === MovementTypes.Discharge ||
    movement.movement_type_id === MovementTypes.DischargeAdjustment;

  const navigate = useNavigate();
  const printRef: React.RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
  const workOrderRef = useRef<WorkOrderMovementHandle>(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    copyStyles: true,
  });
  const hasArticlesLoaded = Array.isArray(movement.articles);
  const resolvedPrintMovement = printMovement ?? movement;
  const isCancelled = Boolean(movement.deleted_at || isCancelledLocally);
  const isSuperadmin = userData?.role === Roles.SUPERADMIN;
  const isDraftMovement =
    movement.movement_type_id === MovementTypes.IncomeDraft ||
    movement.movement_type_id === MovementTypes.DischargeDraft ||
    movement.movement_type_id === MovementTypes.PreparationDraft;
  const canOpenEdit =
    !isCancelled &&
    (isDraftMovement ||
      (isSuperadmin &&
        (movement.movement_type_id === MovementTypes.Income ||
          movement.movement_type_id === MovementTypes.IncomeAdjustment ||
          movement.movement_type_id === MovementTypes.Discharge ||
          movement.movement_type_id === MovementTypes.DischargeAdjustment ||
          movement.movement_type_id === MovementTypes.Preparation)));

  const canCancelMovement =
    isSuperadmin &&
    !isCancelled &&
    (movement.movement_type_id === MovementTypes.Income ||
      movement.movement_type_id === MovementTypes.IncomeAdjustment ||
      movement.movement_type_id === MovementTypes.Discharge ||
      movement.movement_type_id === MovementTypes.DischargeAdjustment ||
      movement.movement_type_id === MovementTypes.Transfer ||
      movement.movement_type_id === MovementTypes.Preparation ||
      movement.movement_type_id === MovementTypes.Additional);

  const canAddAdditionalItems =
    !isClient &&
    (movement.movement_type_id === MovementTypes.Income ||
      movement.movement_type_id === MovementTypes.Discharge ||
      movement.movement_type_id === MovementTypes.Preparation);

  const handleClickEdit = async () => {
    if (!canOpenEdit) return;

    if (
      movement.movement_type_id === MovementTypes.IncomeDraft ||
      movement.movement_type_id === MovementTypes.PreparationDraft ||
      movement.movement_type_id === MovementTypes.DischargeDraft
    ) {
      navigate(
        (movement.movement_type_id === MovementTypes.IncomeDraft
          ? ROUTES.INCOME_WITH_DRAFT
          : ROUTES.DISCHARGE_WITH_DRAFT
        ).replace(":movement_draft_id", movement.id?.toString() || "")
      );
      return;
    }

    if (hasArticlesLoaded) {
      setEditableMovement(movement);
      setIsEditOpen(true);
      return;
    }

    setEditLoading(true);

    try {
      const resp = await getMovementById(String(movement.id), true);
      const fetched = resolveFetchedMovement(resp.data);

      if (!fetched) {
        return;
      }

      setEditableMovement({
        ...fetched,
        articles: Array.isArray(fetched.articles) ? fetched.articles : [],
      });
      setIsEditOpen(true);
    } finally {
      setEditLoading(false);
    }
  };

  const handleClickDelete = () => {
    if (movementToDelete) onDelete(movementToDelete);
  };

  const handleClickCancel = async (
    restoreTargetStatus?: "available" | "not_available",
  ) => {
    if (!movementToCancel || isCancelled) return false;

    setCancelSubmitting(true);
    const wasCancelled = await onCancel(movementToCancel, restoreTargetStatus);
    setCancelSubmitting(false);

    if (wasCancelled) {
      setIsCancelledLocally(true);
    }

    return wasCancelled;
  };

  const shouldShowTotals =
    movement.movement_type_id !== MovementTypes.Transfer &&
    movement.movement_type_id !== MovementTypes.Additional;

  const createdByName = useMemo(() => {
    const id = (movement as Movement).created_by;
    if (!id) return "-";
    const u = users?.find((x) => x.id === id);
    return u ? `${u.first_name} ${u.last_name}` : `#${id}`;
  }, [movement, users]);

  const handleOrderAccordionChange = useCallback(
    async (expanded: boolean) => {
      setOrderAccordionOpen(expanded);

      if (!expanded || !shouldShowOrderAccordion || !movement.order_id) {
        return;
      }

      if (orderLoading || orderMovement?.id === movement.order_id) {
        return;
      }

      setOrderLoading(true);

      try {
        const resp = await getMovementById(String(movement.order_id), true);
        const fetched = resolveFetchedMovement(resp.data);

        setOrderMovement(fetched ?? null);
      } finally {
        setOrderLoading(false);
      }
    },
    [
      getMovementById,
      movement.order_id,
      orderLoading,
      orderMovement?.id,
      shouldShowOrderAccordion,
    ],
  );

  const handleDischargeAccordionChange = useCallback(
    async (expanded: boolean) => {
      setDischargeAccordionOpen(expanded);

      if (
        !expanded ||
        !shouldShowDischargeAccordion ||
        !movement.discharge_movement_id
      ) {
        return;
      }

      if (
        dischargeLoading ||
        dischargeMovement?.id === movement.discharge_movement_id
      ) {
        return;
      }

      setDischargeLoading(true);

      try {
        const resp = await getMovementById(
          String(movement.discharge_movement_id),
          true,
        );
        const fetched = resolveFetchedMovement(resp.data);

        setDischargeMovement(fetched ?? null);
      } finally {
        setDischargeLoading(false);
      }
    },
    [
      dischargeLoading,
      dischargeMovement?.id,
      getMovementById,
      movement.discharge_movement_id,
      shouldShowDischargeAccordion,
    ],
  );

  const handleRelatedAdditionalsAccordionChange = useCallback(
    async (expanded: boolean) => {
      setRelatedAdditionalsAccordionOpen(expanded);

      if (
        !expanded ||
        !shouldShowRelatedAdditionals ||
        relatedAdditionalsLoading ||
        relatedAdditionalMovements !== null
      ) {
        return;
      }

      setRelatedAdditionalsLoading(true);

      try {
        const response = await getRelatedAdditionalMovements(undefined, true);

        setRelatedAdditionalMovements(
          Array.isArray(response.data) ? response.data : [],
        );
      } finally {
        setRelatedAdditionalsLoading(false);
      }
    },
    [
      getRelatedAdditionalMovements,
      relatedAdditionalMovements,
      relatedAdditionalsLoading,
      shouldShowRelatedAdditionals,
    ],
  );

  const handleRelatedAdditionalUpdate = useCallback(
    (updatedMovement: Movement) => {
      setRelatedAdditionalMovements((current) =>
        current?.map((relatedMovement) =>
          relatedMovement.id === updatedMovement.id
            ? updatedMovement
            : relatedMovement,
        ) ?? null,
      );
      onUpdate(updatedMovement);
    },
    [onUpdate],
  );

  const handleArticlesAccordionChange = useCallback(
    async (expanded: boolean) => {
      setArticlesAccordionOpen(expanded);

      if (!expanded || articlesLoading || movementArticlesDetail) {
        return;
      }

      setArticlesLoading(true);

      try {
        const resp = await getMovementArticlesByMovementId(
          `movement/${movement.id}`,
          true
        );

        setMovementArticlesDetail(
          Array.isArray(resp.data) ? resp.data : resp.data?.result ?? []
        );
      } finally {
        setArticlesLoading(false);
      }
    },
    [
      articlesLoading,
      getMovementArticlesByMovementId,
      movement.id,
      movementArticlesDetail,
    ]
  );

  const handleOpenObservationRecords = useCallback(
    async (movementArticle: MovementArticle) => {
      const articleLocation = movementArticle.article_locations as
        | ArticleLocation
        | undefined;

      if (!articleLocation?.id) {
        return;
      }

      const contexts = getMovementArticleObservationFetchContexts(
        movement,
        movementArticle,
      );

      if (!contexts.length) {
        return;
      }

      setSelectedObservationArticle(movementArticle);
      setObservationRecordsOpen(true);
      setObservationRecordsLoading(true);

      try {
        const responses = await Promise.all(
          contexts.map((context) =>
            axiosGet(
              getArticleLocationObservationRecordsUrl(articleLocation.id),
              true,
              buildObservationRecordsQueryParams(context),
            ),
          ),
        );

        const stockUnits = responses.flatMap((response) =>
          response.status === 200
            ? ((response.data ?? []) as ArticleLocationObservationRecordStockUnit[])
            : [],
        );

        setObservationRecords(
          buildObservationRecordItemsFromStockUnits(stockUnits),
        );
      } catch (error) {
        console.error(error);
        setObservationRecords([]);
      } finally {
        setObservationRecordsLoading(false);
      }
    },
    [movement],
  );

  const handleCloseObservationRecords = useCallback(() => {
    setObservationRecordsOpen(false);
    setSelectedObservationArticle(null);
    setObservationRecords([]);
    setObservationRecordsLoading(false);
    setSavingObservationRecordId(null);
  }, []);

  const handleSaveObservationRecord = useCallback(
    async ({
      recordId,
      observation,
      quantityMode,
      specificQuantity,
    }: {
      recordId: string;
      observation: string;
      quantityMode: "total" | "specific";
      specificQuantity: number | null;
    }) => {
      const articleLocation = selectedObservationArticle?.article_locations as
        | ArticleLocation
        | undefined;

      if (!articleLocation?.id) {
        return;
      }

      const targetRecord = observationRecords.find(
        (record) => record.id === recordId,
      );

      if (!targetRecord) {
        return;
      }

      const targetQuantity =
        quantityMode === "specific"
          ? Number(specificQuantity ?? 0)
          : targetRecord.stock_unit_ids.length;

      if (
        !Number.isFinite(targetQuantity) ||
        targetQuantity <= 0 ||
        targetQuantity > targetRecord.stock_unit_ids.length
      ) {
        return;
      }

      setSavingObservationRecordId(recordId);

      try {
        const response = await axiosPut(
          getArticleLocationObservationRecordsUrl(articleLocation.id),
          {
            status: targetRecord.status,
            stock_unit_ids: targetRecord.stock_unit_ids.slice(0, targetQuantity),
            observation:
              observation.trim().length > 0 ? observation.trim() : null,
            reserved_movement_id:
              targetRecord.status === "reserved"
                ? Number(targetRecord.reserved_movement_id ?? 0) || null
                : null,
            discharged_movement_id:
              targetRecord.status === "discharged"
                ? Number(targetRecord.discharged_movement_id ?? 0) || null
                : null,
          },
          true,
        );

        if (response.status !== 200) {
          return;
        }

        const updatedStockUnits = (response.data ??
          []) as ArticleLocationObservationRecordStockUnit[];

        setObservationRecords(
          buildObservationRecordItemsFromStockUnits(updatedStockUnits),
        );
      } catch (error) {
        console.error(error);
      } finally {
        setSavingObservationRecordId(null);
      }
    },
    [observationRecords, selectedObservationArticle],
  );

  const handleClickPrint = useCallback(async () => {
    setPrintLoading(true);

    try {
      if (isWorkOrder) {
        const orderDetail = await workOrderRef.current?.getDetail();
        if (!orderDetail) return;
        const response = await getMovementById(String(movement.id), true);
        const nextPrintMovement = resolveFetchedMovement(response.data) ?? movement;
        setPrintMovement(nextPrintMovement);
        setPrintWorkOrder(orderDetail);
        setPrintPending(true);
        return;
      }
      let nextPrintMovement = movement;

      if (!hasArticlesLoaded && movementArticlesDetail?.length) {
        nextPrintMovement = {
          ...movement,
          articles: movementArticlesDetail,
        };
      } else if (!hasArticlesLoaded) {
        const response = await getMovementById(String(movement.id), true);
        nextPrintMovement = resolveFetchedMovement(response.data) ?? movement;
      }

      let nextRelatedAdditionalMovements = relatedAdditionalMovements ?? [];

      if (
        shouldShowRelatedAdditionals &&
        relatedAdditionalMovements === null
      ) {
        const response = await getRelatedAdditionalMovements(undefined, true);

        nextRelatedAdditionalMovements = Array.isArray(response.data)
          ? response.data
          : [];
        setRelatedAdditionalMovements(nextRelatedAdditionalMovements);
      }

      setPrintMovement(nextPrintMovement);
      setPrintRelatedAdditionalMovements(nextRelatedAdditionalMovements);
      setPrintPending(true);
    } finally {
      setPrintLoading(false);
    }
  }, [
    getMovementById,
    getRelatedAdditionalMovements,
    hasArticlesLoaded,
    movement,
    movementArticlesDetail,
    relatedAdditionalMovements,
    shouldShowRelatedAdditionals,
    isWorkOrder,
  ]);

  const handleCreateAdditionalMovements = async ({
    movement: additionalMovementHeader,
    items,
  }: MovementAdditionalItemsConfirmPayload) => {
    setAdditionalItemsModalOpen(false);

    if (!items.length || !movement.id) {
      return;
    }

    const additionalMovementsPayload = buildAdditionalMovementsPayload({
      baseMovement: {
        ...defaultMovement,
        created_by: userData?.id ?? movement.created_by,
        updated_by: userData?.id ?? movement.updated_by,
      },
      headerMovement: additionalMovementHeader,
      items,
      originMovementId: movement.id,
    });

    const response = await createMovement(additionalMovementsPayload, true);

    if (response.status !== 200) {
      showSnackBar(
        response.data?.message ||
          "No se pudieron crear los adicionales asociados.",
        "error",
      );
      return;
    }

    if (response.status === 200) {
      const refreshed = await getMovementById(String(movement.id), true);
      if (Array.isArray(response.data)) {
        onAdditionalMovementsCreated?.(response.data);
      }
      showSnackBar("Adicionales creados correctamente.", "success");
      const updatedMovement = resolveFetchedMovement(refreshed.data);

      if (updatedMovement) {
        onUpdate(updatedMovement);
      }

      return;
    }
  };

  useEffect(() => {
    if (!shouldShowOrderAccordion) {
      setOrderAccordionOpen(false);
      setOrderMovement(null);
      setOrderLoading(false);
      return;
    }

    if (orderMovement?.id !== movement.order_id) {
      setOrderAccordionOpen(false);
      setOrderMovement(null);
    }
  }, [movement.order_id, orderMovement?.id, shouldShowOrderAccordion]);

  useEffect(() => {
    if (!shouldShowDischargeAccordion) {
      setDischargeAccordionOpen(false);
      setDischargeMovement(null);
      setDischargeLoading(false);
      return;
    }

    if (dischargeMovement?.id !== movement.discharge_movement_id) {
      setDischargeAccordionOpen(false);
      setDischargeMovement(null);
    }
  }, [
    dischargeMovement?.id,
    movement.discharge_movement_id,
    shouldShowDischargeAccordion,
  ]);

  useEffect(() => {
    setRelatedAdditionalsAccordionOpen(false);
    setRelatedAdditionalMovements(null);
    setRelatedAdditionalsLoading(false);
  }, [movement.id, relatedAdditionalMovementIdsKey]);

  useEffect(() => {
    setArticlesAccordionOpen(false);
    setAdditionalItemsAccordionOpen(false);
    setMovementArticlesDetail(null);
    setArticlesLoading(false);
    setSelectedObservationArticle(null);
    setObservationRecordsOpen(false);
    setObservationRecords([]);
    setObservationRecordsLoading(false);
    setSavingObservationRecordId(null);
  }, [movement.id]);

  useEffect(() => {
    setPrintMovement(null);
    setPrintWorkOrder(null);
    setPrintRelatedAdditionalMovements([]);
    setPrintLoading(false);
    setPrintPending(false);
  }, [movement.id, relatedAdditionalMovementIdsKey]);

  useEffect(() => {
    if (!printPending || !resolvedPrintMovement || (isWorkOrder && !printWorkOrder)) {
      return;
    }

    handlePrint();
    setPrintPending(false);
  }, [handlePrint, isWorkOrder, printPending, printWorkOrder, resolvedPrintMovement]);

  return (
    <React.Fragment key={movement.id}>
      <PrintLayout
        ref={printRef}
        headerInfo={
          <OperationPrintHeaderDetails movement={resolvedPrintMovement} stores={stores} />
        }
        footerInfo={
          printRelatedAdditionalMovements.length === 0 ? (
            <OperationPrintTotals movement={resolvedPrintMovement} />
          ) : undefined
        }
      >
        {isWorkOrder ? (
          printWorkOrder && (
            <WorkOrderOperationsDetails
              movement={resolvedPrintMovement}
              order={printWorkOrder}
            />
          )
        ) : (
          <OperationPrintDetails
            client={resolvedPrintMovement.client || null}
            movement={resolvedPrintMovement}
            users={users}
          />
        )}
        {printRelatedAdditionalMovements.length > 0 && (
          <OperationPrintTotals movement={resolvedPrintMovement} />
        )}
        {printRelatedAdditionalMovements.map((additionalMovement) => (
          <Box
            key={additionalMovement.id}
            sx={{
              breakBefore: "page",
              pageBreakBefore: "always",
              pt: 2,
            }}
          >
            <Box sx={{ breakInside: "avoid", pageBreakInside: "avoid" }}>
              <OperationPrintHeaderDetails
                movement={additionalMovement}
                stores={stores}
              />
            </Box>
            <Box py={2}>
              <OperationPrintDetails
                client={additionalMovement.client || null}
                movement={additionalMovement}
                users={users}
              />
            </Box>
            <OperationPrintTotals movement={additionalMovement} />
          </Box>
        ))}
      </PrintLayout>
      <Grid item xs={12}>
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            padding: "1rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Chip
                label={operationTypeName(
                  movement.movement_type_id,
                  movement.movement_order?.order_status,
                )}
                color={operationTypeChipColor(movement.movement_type_id)}
              />
              {isCancelled && (
                <Chip label="Anulado" color="default" variant="outlined" />
              )}
            </Box>
            <Box>
              {canCancelMovement && (
                  <Tooltip title="Anular movimiento" arrow>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setMovementToCancel(movement.id);
                        setOrderRestoreStatus("available");
                        setCancelModalOpen(true);
                      }}
                    >
                      <MdDelete />
                    </IconButton>
                  </Tooltip>
              )}
              {canAddAdditionalItems && (
                <Tooltip title="Agregar adicionales" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setAdditionalItemsModalOpen(true)}
                  >
                    <MdAdd />
                  </IconButton>
                </Tooltip>
              )}
              {(movement.movement_type_id === MovementTypes.Income ||
                movement.movement_type_id === MovementTypes.IncomeAdjustment ||
                movement.movement_type_id === MovementTypes.Discharge ||
                movement.movement_type_id === MovementTypes.Preparation ||
                movement.movement_type_id === MovementTypes.DischargeAdjustment ||
                movement.movement_type_id === MovementTypes.IncomeDraft ||
                movement.movement_type_id === MovementTypes.DischargeDraft) &&
                canOpenEdit && (
                  <Tooltip title="Editar movimiento" arrow>
                    <IconButton
                      size="small"
                      onClick={() => {
                        void handleClickEdit();
                      }}
                      disabled={editLoading}
                    >
                      <MdEdit />
                    </IconButton>
                  </Tooltip>
                )}
              {(movement.movement_type_id === MovementTypes.IncomeDraft ||
                movement.movement_type_id === MovementTypes.DischargeDraft ||
                movement.movement_type_id ===
                  MovementTypes.PreparationDraft) &&
                !isCancelled && (
                  <Tooltip title="Eliminar borrador" arrow>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setMovementToDelete(movement.id);
                        setDeleteModalOpen(true);
                      }}
                    >
                      <MdDelete />
                    </IconButton>
                  </Tooltip>
                )}
              {movement.movement_type_id !== MovementTypes.IncomeDraft &&
                movement.movement_type_id !== MovementTypes.PreparationDraft &&
                movement.movement_type_id !== MovementTypes.DischargeDraft && (
                  <Tooltip title="Imprimir movimiento" arrow>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => {
                          void handleClickPrint();
                        }}
                        disabled={printLoading}
                      >
                        <MdPrint />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
            </Box>
          </Box>
          <Typography variant="body2" fontWeight={600}>
            Datos de la Operación
          </Typography>
          <TableContainer component={Paper} elevation={4} sx={{ p: 1 }}>
            <Table>
              <TableHead
                sx={{
                  "*": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textWrap: "nowrap",
                  },
                }}
              >
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>N° Movimiento</TableCell>
                  {movement.movement_type_id === MovementTypes.Additional && (
                    <TableCell>Movimiento asociado</TableCell>
                  )}
                  <TableCell>Creado por</TableCell>
                  <TableCell>Doc. Cliente</TableCell>
                  <TableCell>Numero de Remito</TableCell>
                  <TableCell>Operación</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>
                    {movement.movement_type_id === MovementTypes.Transfer
                      ? "Nro. de Planilla"
                      : "Cliente Asociado"}
                  </TableCell>
                  <TableCell>Almacén</TableCell>
                  <TableCell>Peso Total</TableCell>
                  <TableCell>Pallets Totales</TableCell>
                  <TableCell>Observaciones</TableCell>
                  {shouldShowTotals && <TableCell>Cant. Total</TableCell>}
                  {shouldShowTotals && <TableCell>Total Unidad</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover>
                  <TableCell>{formatDateOnlyDMY(movement.date)}</TableCell>
                  <TableCell>{movement.id}</TableCell>
                  {movement.movement_type_id === MovementTypes.Additional && (
                    <TableCell>
                      {movement.movement_id ? `#${movement.movement_id}` : "-"}
                    </TableCell>
                  )}
                  <TableCell>{createdByName}</TableCell>
                  <TableCell>{movement.client_document || "-"}</TableCell>
                  <TableCell>{movement.numero_remito}</TableCell>
                  <TableCell>
                    {operationTypeName(
                      movement.movement_type_id,
                      movement.movement_order?.order_status,
                    )}
                  </TableCell>
                  <TableCell>{movement.client?.razon_social}</TableCell>
                  <TableCell>
                    {movement.movement_type_id === MovementTypes.Transfer
                      ? movement.client_document
                      : movement.associated_client}
                  </TableCell>
                  <TableCell>
                    {movement.store_id == null
                      ? "Todos"
                      : stores.find((store) => movement.store_id === store.id)
                          ?.description || "-"}
                  </TableCell>
                  <TableCell>
                    {movement.movement_type_id === MovementTypes.Income ||
                    movement.movement_type_id === MovementTypes.Discharge ||
                    movement.movement_type_id === MovementTypes.Preparation ||
                    isWorkOrder
                      ? movement.weight
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    {" "}
                    {movement.movement_type_id === MovementTypes.Income ||
                    movement.movement_type_id === MovementTypes.Discharge ||
                    movement.movement_type_id === MovementTypes.Preparation ||
                    isWorkOrder
                      ? movement.pallets
                      : "N/A"}
                  </TableCell>
                  <TableCell>{movement.observation?.trim() || "-"}</TableCell>
                  {shouldShowTotals && (
                    <TableCell>{movement.total_quantity ?? "-"}</TableCell>
                  )}
                  {shouldShowTotals && (
                    <TableCell>{movement.total_units ?? "-"}</TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {shouldShowArticlesAccordion && (
            <Accordion
              expanded={articlesAccordionOpen}
              onChange={(_, expanded) => {
                void handleArticlesAccordionChange(expanded);
              }}
            >
              <AccordionSummary expandIcon={<MdExpandMore />}>
                <Typography variant="body2" fontWeight={600}>
                  Listado de Productos
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {articlesLoading ? (
                  <Typography variant="body2">Cargando artí­culos...</Typography>
                ) : movementArticlesDetail?.length ? (
                  <TableContainer component={Paper} elevation={4} sx={{ p: 1 }}>
                    <Table>
                      <TableHead>
                        <TableRow
                          sx={{
                            "*": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textWrap: "nowrap",
                            },
                          }}
                        >
                          <TableCell>Código de Artículo</TableCell>
                          <TableCell>Cantidad</TableCell>
                          <TableCell>Descripción</TableCell>
                          <TableCell>Cantidad por Contenedor</TableCell>
                          <TableCell>Contenedor</TableCell>
                          <TableCell>UM</TableCell>
                          <TableCell>Presentación</TableCell>
                          <TableCell>Código de Cliente</TableCell>
                          {isIncomeLikeMovement && (
                            <TableCell>Estado mercadería</TableCell>
                          )}
                          {movement.show_lote && <TableCell>Lote</TableCell>}
                          {movement.show_oc && <TableCell>OC</TableCell>}
                          {movement.show_fecha_vencimiento && (
                            <TableCell>Fecha Venc.</TableCell>
                          )}
                          <TableCell>Observación</TableCell>
                          <TableCell>Subtotal (Kg)</TableCell>
                          <TableCell>Subtotal (Un)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {movementArticlesDetail.map((movementArticle, index) => (
                          <TableRow hover key={index}>
                            <TableCell>{movementArticle.code}</TableCell>
                            <TableCell>{movementArticle.amount}</TableCell>
                            <TableCell>{movementArticle.description}</TableCell>
                            <TableCell>
                              {movementArticle.article_locations
                                ?.amount_per_container}
                            </TableCell>
                            <TableCell>
                              {movementArticle.article_locations?.article
                                ?.container?.description || "-"}
                            </TableCell>
                            <TableCell>
                              {movementArticle.article_locations?.article?.unit
                                ?.description || "-"}
                            </TableCell>
                            <TableCell>
                              {movementArticle.presentation ??
                                movementArticle.article_locations?.article?.presentation ??
                                "-"}
                            </TableCell>
                            <TableCell>
                              {movementArticle.article_locations?.article?.client?.code ?? "-"}
                            </TableCell>
                            {isIncomeLikeMovement && (
                              <TableCell>
                                {getMovementArticleStockStateLabel(
                                  movementArticle.stock_state,
                                )}
                              </TableCell>
                            )}
                            {movement.show_lote && (
                              <TableCell>
                                {movementArticle.article_locations?.lote ||
                                  "No asignado"}
                              </TableCell>
                            )}
                            {movement.show_oc && (
                              <TableCell>
                                {movementArticle.article_locations?.oc ||
                                  "No asignado"}
                              </TableCell>
                            )}
                            {movement.show_fecha_vencimiento && (
                              <TableCell>
                                {movementArticle.article_locations
                                  ?.fecha_vencimiento
                                  ? formatDateOnlyDMY(
                                      movementArticle.article_locations
                                        .fecha_vencimiento
                                    )
                                  : "No asignado"}
                              </TableCell>
                            )}
                            <TableCell align="center">
                              <Tooltip
                                title={
                                  isDischargeLikeMovement
                                    ? "Ver observaciones"
                                    : "Ver y editar observaciones"
                                }
                                arrow
                              >
                                <Box component="span">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      void handleOpenObservationRecords(
                                        movementArticle,
                                      );
                                    }}
                                  >
                                    <MdVisibility />
                                  </IconButton>
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{movementArticle.subtotal_kg}</TableCell>
                            <TableCell>{movementArticle.subtotal}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay artículos relacionados.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          )}
          {false && movement.articles?.length > 0 && (
            <>
              <Typography variant="body2" fontWeight={600}>
                Listado de Productos
              </Typography>
              <TableContainer component={Paper} elevation={4} sx={{ p: 1 }}>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        "*": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          textWrap: "nowrap",
                        },
                      }}
                    >
                      <TableCell>Código de Articulo</TableCell>
                      <TableCell>Cantidad</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell>Cantidad por Contenedor</TableCell>
                      <TableCell>Contenedor</TableCell>
                      <TableCell>UM</TableCell>
                      <TableCell>Presentación</TableCell>
                      <TableCell>Código de Cliente</TableCell>
                      {movement.show_lote && <TableCell>Lote</TableCell>}
                      {movement.show_oc && <TableCell>OC</TableCell>}
                      {movement.show_fecha_vencimiento && (
                        <TableCell>Fecha Venc.</TableCell>
                      )}
                      <TableCell>Observación</TableCell>
                      <TableCell>Subtotal (Kg)</TableCell>
                      <TableCell>Subtotal (Un)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {movement.articles?.map((movementArticle, index) => (
                      <TableRow hover key={index}>
                        <TableCell>{movementArticle.code}</TableCell>
                        <TableCell>{movementArticle.amount}</TableCell>
                        <TableCell>{movementArticle.description}</TableCell>
                        <TableCell>
                          {movementArticle.article_locations?.amount_per_container}
                        </TableCell>
                        <TableCell>
                          {movementArticle.article_locations?.article?.container
                            ?.description || "-"}
                        </TableCell>
                        <TableCell>
                          {movementArticle.article_locations?.article?.unit
                            ?.description || "-"}
                        </TableCell>
                        <TableCell>
                          {movementArticle.presentation ??
                            movementArticle.presentation ??
                            "-"}
                        </TableCell>
                        <TableCell>{movement.client?.code}</TableCell>
                        {movement.show_lote && (
                          <TableCell>
                            {movementArticle.article_locations?.lote ||
                              "No asignado"}
                          </TableCell>
                        )}
                        {movement.show_oc && (
                          <TableCell>
                            {movementArticle.article_locations?.oc ||
                              "No asignado"}
                          </TableCell>
                        )}
                        {movement.show_fecha_vencimiento && (
                          <TableCell>
                            {movementArticle.article_locations
                              ?.fecha_vencimiento
                              ? formatDateOnlyDMY(
                                  movementArticle.article_locations
                                    .fecha_vencimiento
                                )
                              : "No asignado"}
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <Tooltip title="Ver y editar observaciones" arrow>
                            <Box component="span">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  void handleOpenObservationRecords(
                                    movementArticle,
                                  );
                                }}
                              >
                                <MdVisibility />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{movementArticle.subtotal_kg}</TableCell>
                        <TableCell>{movementArticle.subtotal}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {shouldShowAdditionalItemsAccordion && (
            <Accordion
              expanded={additionalItemsAccordionOpen}
              onChange={(_, expanded) => {
                setAdditionalItemsAccordionOpen(expanded);
              }}
            >
              <AccordionSummary expandIcon={<MdExpandMore />}>
                <Typography variant="body2" fontWeight={600}>
                  Listado de Adicionales
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper} elevation={4} sx={{ p: 1 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Descripción</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Cantidad</TableCell>
                      <TableCell>Precio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {movement.additional_items?.map(
                      (movementAdditionalItem, index) => (
                        <TableRow hover key={index}>
                          <TableCell>
                            {
                              movementAdditionalItem.additional_item
                                ?.description
                            }
                          </TableCell>
                          <TableCell>
                            {movementAdditionalItem.additional_item
                              ?.client_id || "Todos"}
                          </TableCell>
                          <TableCell>{movementAdditionalItem.amount}</TableCell>
                          <TableCell>{movementAdditionalItem.price ?? 0}</TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {shouldShowRelatedAdditionals && (
            <Accordion
              expanded={relatedAdditionalsAccordionOpen}
              onChange={(_, expanded) => {
                void handleRelatedAdditionalsAccordionChange(expanded);
              }}
              sx={{ mt: 2 }}
            >
              <AccordionSummary expandIcon={<MdExpandMore />}>
                <Typography variant="body2" fontWeight={600}>
                  Adicionales relacionados ({relatedAdditionalMovementIds
                    .map((id) => `#${id}`)
                    .join(", ")})
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                {relatedAdditionalsLoading ? (
                  <Typography variant="body2">
                    Cargando adicionales...
                  </Typography>
                ) : relatedAdditionalMovements?.length ? (
                  <Grid container gap={2}>
                    {relatedAdditionalMovements.map((additionalMovement) => (
                      <OperationsLogsCard
                        key={additionalMovement.id}
                        movement={additionalMovement}
                        client={client}
                        stores={stores}
                        users={users}
                        associatedClients={associatedClients}
                        onUpdate={handleRelatedAdditionalUpdate}
                        onDelete={onDelete}
                        onCancel={onCancel}
                        hideRelatedMovementAccordions
                      />
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No se pudieron obtener los adicionales relacionados.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {shouldShowOrderAccordion && (
            <Accordion
              expanded={orderAccordionOpen}
              onChange={(_, expanded) => {
                void handleOrderAccordionChange(expanded);
              }}
              sx={{ mt: 2 }}
            >
              <AccordionSummary expandIcon={<MdExpandMore />}>
                <Typography variant="body2" fontWeight={600}>
                  Pedido relacionado (#{movement.order_id})
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                {orderLoading ? (
                  <Typography variant="body2">Cargando pedido...</Typography>
                ) : orderMovement ? (
                  <OperationsLogsCard
                    movement={orderMovement}
                    client={client}
                    stores={stores}
                    users={users}
                    associatedClients={associatedClients}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    onCancel={onCancel}
                    hideRelatedMovementAccordions
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No se pudo obtener el pedido relacionado.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {shouldShowDischargeAccordion && (
            <Accordion
              expanded={dischargeAccordionOpen}
              onChange={(_, expanded) => {
                void handleDischargeAccordionChange(expanded);
              }}
              sx={{ mt: 2 }}
            >
              <AccordionSummary expandIcon={<MdExpandMore />}>
                <Typography variant="body2" fontWeight={600}>
                  Egreso relacionado (#{movement.discharge_movement_id})
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                {dischargeLoading ? (
                  <Typography variant="body2">Cargando egreso...</Typography>
                ) : dischargeMovement ? (
                  <OperationsLogsCard
                    movement={dischargeMovement}
                    client={client}
                    stores={stores}
                    users={users}
                    associatedClients={associatedClients}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    onCancel={onCancel}
                    hideRelatedMovementAccordions
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No se pudo obtener el egreso relacionado.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {isWorkOrder && (
            <WorkOrderMovement
              key={movement.movement_work_order?.id ?? movement.id}
              ref={workOrderRef}
              workOrder={movement.movement_work_order}
            />
          )}
        </Paper>
      </Grid>
      {editableMovement && (
        <EditMovementModal
          open={isEditOpen}
          onClose={() => {
            setIsEditOpen(false);
            setEditableMovement(null);
          }}
          movement={editableMovement}
          stores={stores}
          onUpdate={(updatedMovement) => {
            setEditableMovement(updatedMovement);
            onUpdate(updatedMovement);
          }}
        />
      )}
      <InventoryAdjustmentModal
        open={isAdjustOpen}
        onClose={() => setIsAdjustOpen(false)}
        movement={movement}
      />
      <ObservationRecordsModal
        open={observationRecordsOpen}
        loading={observationRecordsLoading}
        savingRecordId={savingObservationRecordId}
        title="Observaciones"
        records={observationRecords}
        readOnly={isDischargeLikeMovement}
        isRecordEditable={(record) => record.status !== "transferred"}
        onClose={handleCloseObservationRecords}
        onSaveRecord={handleSaveObservationRecord}
      />
      <DeleteConfirmationModaL
        open={deleteModalOpen}
        handleOpen={() => setDeleteModalOpen(!deleteModalOpen)}
        entityName={ENTITY.MOVEMENTS}
        values={[movementToDelete?.toString() || ""]}
        handleDelete={handleClickDelete}
      />
      {!isCancelled &&
        movement.movement_type_id !== MovementTypes.Preparation && (
        <DeleteConfirmationModaL
          open={cancelModalOpen}
          handleOpen={() => setCancelModalOpen(!cancelModalOpen)}
          entityName={ENTITY.MOVEMENTS}
          values={[movementToCancel?.toString() || ""]}
          handleDelete={() => {
            void handleClickCancel();
          }}
        />
      )}
      <MovementAdditionalItemsStepModal
        open={additionalItemsModalOpen}
        title={getAdditionalItemsModalTitle(movement, true)}
        movement={movement}
        additionalItems={clientAdditionalItems}
        childClients={childClients}
        associatedClients={associatedClients}
        onClose={() => setAdditionalItemsModalOpen(false)}
        onConfirm={handleCreateAdditionalMovements}
      />
      {!isCancelled &&
        movement.movement_type_id === MovementTypes.Preparation && (
        <Dialog
          open={cancelModalOpen}
          onClose={() => {
            if (cancelSubmitting) return;
            setCancelModalOpen(false);
            setOrderRestoreStatus("available");
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Anular pedido a preparar</DialogTitle>
          <DialogContent>
            <Box pt={1} display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2">
                Al anular el pedido, indique si la mercaderia vuelve a estar
                disponible o no disponible.
              </Typography>

              <FormControl>
                <RadioGroup
                  value={orderRestoreStatus}
                  onChange={(event) =>
                    setOrderRestoreStatus(
                      event.target.value as "available" | "not_available",
                    )
                  }
                >
                  <FormControlLabel
                    value="available"
                    control={<Radio />}
                    label="Disponible"
                  />
                  <FormControlLabel
                    value="not_available"
                    control={<Radio />}
                    label="No disponible"
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setCancelModalOpen(false);
                setOrderRestoreStatus("available");
              }}
              disabled={cancelSubmitting}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={cancelSubmitting}
              onClick={async () => {
                const wasCancelled = await handleClickCancel(orderRestoreStatus);
                if (wasCancelled) {
                  setCancelModalOpen(false);
                  setOrderRestoreStatus("available");
                }
              }}
            >
              {cancelSubmitting ? "Anulando..." : "Confirmar"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </React.Fragment>
  );
}
